import { getTimezone } from '../hooks/useTimezone';

export interface ParsedEvent {
  uid: string;
  title: string;
  description?: string;
  dates: string[];
  allDay: boolean;
  startTime?: string;
  endTime?: string;
  location?: string;
}

export function parseICSContent(content: string): ParsedEvent[] {
  const events: ParsedEvent[] = [];
  const eventBlocks = content.match(/BEGIN:VEVENT[\s\S]*?END:VEVENT/g) || [];

  for (const block of eventBlocks) {
    try {
      const event = parseEventBlock(block);
      if (event) {
        events.push(event);
      }
    } catch (e) {
      console.warn('Failed to parse event block:', e);
    }
  }

  return events;
}

function parseEventBlock(block: string): ParsedEvent | null {
  const getField = (name: string): string | null => {
    // Handle folded lines (RFC 5545)
    const unfolded = block.replace(/\r?\n[ \t]/g, '');
    const regex = new RegExp(`^${name}[^:]*:(.+)$`, 'm');
    const match = unfolded.match(regex);
    return match ? match[1].trim() : null;
  };

  const uid = getField('UID');
  const summary = getField('SUMMARY');
  
  if (!uid || !summary) return null;

  // Parse start date/time — extract TZID from params if present
  const unfoldedBlock = block.replace(/\r?\n[ \t]/g, '');
  const dtstartMatch = unfoldedBlock.match(/DTSTART([^:]*):([^\r\n]+)/);
  const dtendMatch = unfoldedBlock.match(/DTEND([^:]*):([^\r\n]+)/);

  if (!dtstartMatch) return null;

  const dtstartParams = dtstartMatch[1];
  const dtstartValue = dtstartMatch[2].trim();
  const startTzid = dtstartParams.match(/TZID=([^;:]+)/)?.[1] || null;

  const { date: startDate, time: startTime, allDay } = parseDateTimeValue(
    dtstartValue,
    dtstartParams.includes('VALUE=DATE') || !dtstartValue.includes('T'),
    startTzid
  );

  let endDate = startDate;
  let endTime = startTime;

  if (dtendMatch) {
    const dtendParams = dtendMatch[1];
    const dtendValue = dtendMatch[2].trim();
    const endTzid = dtendParams.match(/TZID=([^;:]+)/)?.[1] || null;

    const parsed = parseDateTimeValue(
      dtendValue,
      dtendParams.includes('VALUE=DATE') || !dtendValue.includes('T'),
      endTzid
    );
    endDate = parsed.date;
    endTime = parsed.time;
    
    // For all-day events, end date is exclusive in ICS, so subtract 1 day
    if (allDay && endDate > startDate) {
      const d = new Date(endDate);
      d.setDate(d.getDate() - 1);
      endDate = d.toISOString().split('T')[0];
    }
  }

  // Generate dates array
  const dates = generateDateRange(startDate, endDate);

  return {
    uid,
    title: decodeICSString(summary),
    description: getField('DESCRIPTION') ? decodeICSString(getField('DESCRIPTION')!) : undefined,
    dates,
    allDay,
    startTime: allDay ? undefined : startTime,
    endTime: allDay ? undefined : endTime,
    location: getField('LOCATION') ? decodeICSString(getField('LOCATION')!) : undefined,
  };
}

function parseDateTimeValue(
  value: string,
  isDateOnly: boolean,
  tzid?: string | null
): { date: string; time?: string; allDay: boolean } {
  if (isDateOnly || !value.includes('T')) {
    // Date only: 20241225 — no timezone conversion needed
    const cleaned = value.replace(/Z$/, '');
    const year = cleaned.substring(0, 4);
    const month = cleaned.substring(4, 6);
    const day = cleaned.substring(6, 8);
    return { date: `${year}-${month}-${day}`, allDay: true };
  }

  // Date with time: 20241225T143000 or 20241225T143000Z
  const isUTC = value.endsWith('Z');
  const cleaned = value.replace(/Z$/, '');
  const year = parseInt(cleaned.substring(0, 4));
  const month = parseInt(cleaned.substring(4, 6));
  const day = parseInt(cleaned.substring(6, 8));
  const hour = parseInt(cleaned.substring(9, 11));
  const minute = parseInt(cleaned.substring(11, 13));

  const sourceTimezone = isUTC ? 'UTC' : tzid || null;

  if (sourceTimezone) {
    // Convert from source timezone to user's timezone
    const userTimezone = getTimezone();
    const converted = convertTimezone(year, month, day, hour, minute, sourceTimezone, userTimezone);
    return {
      date: converted.date,
      time: converted.time,
      allDay: false,
    };
  }

  // Floating time (no timezone info) — use as-is
  return {
    date: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
    time: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
    allDay: false,
  };
}

/** Parse offset-style timezone IDs like GMT+0100, GMT-0530 → offset in minutes */
function parseGmtOffsetMinutes(tz: string): number | null {
  const m = tz.match(/^GMT([+-])(\d{2})(\d{2})$/);
  if (!m) return null;
  const sign = m[1] === '+' ? 1 : -1;
  return sign * (parseInt(m[2]) * 60 + parseInt(m[3]));
}

/** Convert a date/time from one timezone to another using the Intl API */
function convertTimezone(
  year: number, month: number, day: number,
  hour: number, minute: number,
  fromTz: string, toTz: string
): { date: string; time: string } {
  // Build a UTC timestamp that represents the given time in fromTz
  // by using Intl.DateTimeFormat to figure out the offset
  const dt = new Date(Date.UTC(year, month - 1, day, hour, minute));

  // Handle offset-style TZIDs (e.g. iCloud emits GMT+0100, GMT-0530)
  // Intl.DateTimeFormat only accepts IANA names, so apply the offset manually.
  const fromOffsetMinutes = parseGmtOffsetMinutes(fromTz);
  if (fromOffsetMinutes !== null) {
    // dt is treated as if it were local time in the given offset.
    // Subtract the offset to get the true UTC instant.
    const utcInstant = new Date(dt.getTime() - fromOffsetMinutes * 60_000);
    return formatInTimezone(utcInstant, toTz);
  }

  if (fromTz === 'UTC') {
    // dt is already correct UTC — just format in target timezone
    return formatInTimezone(dt, toTz);
  }

  // For non-UTC IANA source timezones, we need to find what UTC instant corresponds
  // to the given local time in fromTz.
  // Strategy: format a known UTC instant in fromTz, compute the offset, then adjust.
  const fromParts = new Intl.DateTimeFormat('en-CA', {
    timeZone: fromTz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(dt);

  const fromGet = (type: string) => parseInt(fromParts.find(p => p.type === type)?.value || '0');
  const fromLocal = new Date(Date.UTC(fromGet('year'), fromGet('month') - 1, fromGet('day'), fromGet('hour') === 24 ? 0 : fromGet('hour'), fromGet('minute')));

  // Offset in ms: how far ahead fromTz local time is from UTC
  const offsetMs = fromLocal.getTime() - dt.getTime();

  // The actual UTC instant for the source local time
  const utcInstant = new Date(dt.getTime() - offsetMs);

  return formatInTimezone(utcInstant, toTz);
}

function formatInTimezone(utcInstant: Date, tz: string): { date: string; time: string } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(utcInstant);

  const get = (type: string) => parts.find(p => p.type === type)?.value || '00';
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    time: `${get('hour') === '24' ? '00' : get('hour')}:${get('minute')}`,
  };
}

function generateDateRange(start: string, end: string): string[] {
  const dates: string[] = [];
  const current = new Date(start);
  const endDate = new Date(end);
  
  while (current <= endDate) {
    dates.push(current.toISOString().split('T')[0]);
    current.setDate(current.getDate() + 1);
  }
  
  return dates;
}

function decodeICSString(str: string): string {
  return str
    .replace(/\\n/g, '\n')
    .replace(/\\,/g, ',')
    .replace(/\\;/g, ';')
    .replace(/\\\\/g, '\\');
}

export async function fetchICSFromUrl(url: string): Promise<string> {
  let normalizedUrl = url.trim();

  // Normalise webcal:// / webcals:// → https://
  if (normalizedUrl.startsWith('webcal://')) {
    normalizedUrl = normalizedUrl.replace('webcal://', 'https://');
  } else if (normalizedUrl.startsWith('webcals://')) {
    normalizedUrl = normalizedUrl.replace('webcals://', 'https://');
  }

  // Google Calendar: convert sharing/subscribe URLs to ICS feed URLs
  const gcalCidMatch = normalizedUrl.match(/calendar\.google\.com\/calendar\/.*[?&]cid=([^&]+)/);
  if (gcalCidMatch) {
    try {
      const email = atob(gcalCidMatch[1]);
      normalizedUrl = `https://calendar.google.com/calendar/ical/${encodeURIComponent(email)}/public/basic.ics`;
    } catch {
      normalizedUrl = `https://calendar.google.com/calendar/ical/${encodeURIComponent(gcalCidMatch[1])}/public/basic.ics`;
    }
  }

  const gcalEmbedMatch = normalizedUrl.match(/calendar\.google\.com\/calendar\/embed\?.*src=([^&]+)/);
  if (gcalEmbedMatch) {
    const email = decodeURIComponent(gcalEmbedMatch[1]);
    normalizedUrl = `https://calendar.google.com/calendar/ical/${encodeURIComponent(email)}/public/basic.ics`;
  }

  // --- Primary: Supabase edge function (server-side, no CORS issues) ---
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    if (supabaseUrl && supabaseKey) {
      console.debug('[fetch-ics] Trying edge function for:', normalizedUrl);
      const res = await fetch(`${supabaseUrl}/functions/v1/fetch-ics`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({ url: normalizedUrl }),
      });
      console.debug('[fetch-ics] Edge function response:', res.status, res.statusText);
      if (res.ok) {
        const text = await res.text();
        console.debug('[fetch-ics] Edge function content preview:', text.substring(0, 100));
        if (text.includes('BEGIN:VCALENDAR')) return text;
        console.warn('[fetch-ics] Edge function returned non-ICS content');
      } else {
        const body = await res.text();
        console.warn('[fetch-ics] Edge function error body:', body);
      }
    } else {
      console.warn('[fetch-ics] Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY');
    }
  } catch (err) {
    console.warn('[fetch-ics] Edge function fetch failed:', err);
  }

  // --- Fallback: CORS proxies ---
  const corsProxies = [
    (u: string) => `https://api.allorigins.win/raw?url=${encodeURIComponent(u)}`,
    (u: string) => `https://corsproxy.io/?${encodeURIComponent(u)}`,
    (u: string) => `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(u)}`,
  ];

  let lastError: Error | null = null;
  let invalidContentReceived = false;
  let receivedContent = '';

  for (const proxyFn of corsProxies) {
    const proxyUrl = proxyFn(normalizedUrl);
    try {
      console.debug('[fetch-ics] Trying proxy:', proxyUrl);
      const response = await fetch(proxyUrl);
      console.debug('[fetch-ics] Proxy response:', response.status);
      if (!response.ok) {
        lastError = new Error(`HTTP ${response.status}: ${response.statusText}`);
        continue;
      }
      const text = await response.text();
      receivedContent = text.substring(0, 200);
      console.debug('[fetch-ics] Proxy content preview:', receivedContent);
      if (!text.includes('BEGIN:VCALENDAR')) {
        invalidContentReceived = true;
        continue;
      }
      return text;
    } catch (err) {
      console.warn('[fetch-ics] Proxy failed:', proxyUrl, err);
      lastError = err as Error;
    }
  }

  if (invalidContentReceived) {
    const isGoogleLogin = receivedContent.includes('accounts.google.com');
    if (isGoogleLogin) {
      throw new Error('This Google Calendar is not shared publicly. Go to Google Calendar → Settings → your calendar → "Access permissions" → enable "Make available to public", then use the public ICS link.');
    }
    throw new Error('Calendar URL did not return valid ICS data. Make sure the calendar is shared publicly. For iCloud, share the calendar publicly and copy the webcal:// link.');
  }

  throw lastError || new Error('Failed to fetch calendar. Please check the URL and ensure it is publicly accessible.');
}